// client/src/App.tsx
import React, { useEffect, useState, useRef } from 'react';
import LgmProductNavigator from './pages/LgmProductNavigator';
import useAuth from 'auth/auth';
import ColdStartModal from './components/ColdStartModal';
import IpBlockedModal from './components/IpBlockedModal';
import { reportSuiteUsage } from './telemetry/suiteTelemetry';
import { LGM_MAGENTA, LGM_ORANGE } from './theme/colors';
import { GRAY_50, GRAY_600, GRAY_700 } from 'theme/grayScale';
import { loginRequest } from './authConfig';

// Extend Window interface for E2E test flag
declare global {
  interface Window {
    __E2E_TEST__?: boolean;
  }
}

const App: React.FC = () => {
  const { instance, account, inProgress, roles } = useAuth();

  // E2E test bypass: Skip auth checks when __E2E_TEST__ flag is set.
  // Gated to non-production builds so the bypass is dead code in prod and cannot
  // be triggered by setting the flag in a production browser session.
  const isE2ETest =
    typeof window !== 'undefined' &&
    window.__E2E_TEST__ === true &&
    process.env.NODE_ENV !== 'production';

  const [authError, setAuthError] = useState<string | null>(null);
  const [isColdStartActive, setIsColdStartActive] = useState(false);
  const [ipBlockedInfo, setIpBlockedInfo] = useState<{
    isBlocked: boolean;
    ipAddress: string | null;
  }>({
    isBlocked: false,
    ipAddress: null,
  });

  // 🔥 Track if we've had a successful connection (cleared IP block)
  // Once cleared, ignore subsequent blocked events (race condition from delayed requests)
  const hasConnectedSuccessfully = useRef(false);

  useEffect(() => {
    document.title = 'LGM Navigator';

    // Skip auth validation in E2E tests
    if (isE2ETest) return;

    if (inProgress !== 'none') return;

    if (account) {
      const username = account.username || '';

      // ✅ Only allow @lgmpharma.com accounts with roles
      if (!username.toLowerCase().endsWith('@lgmpharma.com')) {
        setAuthError(
          '❌ Only LGM 365 accounts are allowed. You will be redirected.'
        );
        setTimeout(() => instance.logoutRedirect(), 3000);
        return;
      }

      if (roles.length === 0 || roles.includes('No roles assigned')) {
        setAuthError('❌ No role assigned. Please contact an administrator.');
        setTimeout(() => instance.logoutRedirect(), 3000);
        return;
      }

      // Authenticated + role-validated: report usage into the LGM suite's
      // shared Application Insights so Navigator appears in the Intelligence
      // Hub's Suite Usage Analytics. Idempotent — fires once per page load.
      reportSuiteUsage(account);
    }
  }, [instance, account, inProgress, roles, isE2ETest]);

  // 🔥 Listen for cold start events
  useEffect(() => {
    const handleColdStart = (event: CustomEvent) => {
      const status = event.detail?.status;
      if (status === 'waking') {
        setIsColdStartActive(true);
      } else if (status === 'ready') {
        setIsColdStartActive(false);
      }
    };

    window.addEventListener(
      'database-cold-start',
      handleColdStart as EventListener
    );

    return () => {
      window.removeEventListener(
        'database-cold-start',
        handleColdStart as EventListener
      );
    };
  }, []);

  // 🔥 Listen for IP blocked events
  useEffect(() => {
    const handleIpBlocked = (event: CustomEvent) => {
      const ipAddress = event.detail?.ipAddress;
      console.log('[App] 📨 Received IP blocked event', {
        ipAddress,
        hasConnectedSuccessfully: hasConnectedSuccessfully.current,
        timestamp: new Date().toISOString(),
      });

      // If IP address is null, we've successfully connected
      if (ipAddress == null) {
        console.log(
          '[App] ✅ Clearing IP blocked modal - successful connection'
        );
        hasConnectedSuccessfully.current = true;
        setIpBlockedInfo({
          isBlocked: false,
          ipAddress: null,
        });
        return;
      }

      // If we've already connected successfully, ignore subsequent blocked events
      // (these are from delayed/cached requests)
      if (hasConnectedSuccessfully.current) {
        console.log(
          '[App] ⏭️  Ignoring IP blocked event - already connected successfully'
        );
        return;
      }

      // Otherwise, show the blocked modal
      console.log('[App] 🚫 SHOWING IP BLOCKED MODAL', { ipAddress });
      setIpBlockedInfo({
        isBlocked: true,
        ipAddress: ipAddress,
      });
    };

    window.addEventListener(
      'database-ip-blocked',
      handleIpBlocked as EventListener
    );

    return () => {
      window.removeEventListener(
        'database-ip-blocked',
        handleIpBlocked as EventListener
      );
    };
  }, []);

  if (inProgress !== 'none') {
    return (
      <div style={styles.loadingContainerStyle}>
        <p>Loading authentication...</p>
      </div>
    );
  }

  if (authError) {
    return (
      <div style={styles.errorOverlayStyle}>
        <div style={styles.errorModalStyle}>
          <h2 style={styles.errorTitleStyle}>Access Denied</h2>
          <p style={styles.errorTextStyle}>{authError}</p>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div style={styles.loginOverlayStyle}>
        <div style={styles.loginModalStyle}>
          <img
            src="/LGMNavigator.png"
            alt="LGM Navigator"
            style={styles.loginLogoStyle}
          />
          <h1 style={styles.loginTitleStyle}>LGM Navigator</h1>
          <p style={styles.loginSubtitleStyle}>
            Smarter API Sourcing — Fast, Reliable, Global
          </p>
          <button
            style={styles.loginButtonStyle}
            onClick={() => instance.loginRedirect(loginRequest)}>
            🔒 Secure Login
          </button>
        </div>
      </div>
    );
  }

  // ✅ Only render the main navigator. Sub-pages (Grades / TherapeuticAreas)
  // are handled inside LgmProductNavigator.tsx based on navigation state.
  return (
    <>
      <ColdStartModal isVisible={isColdStartActive} />
      <IpBlockedModal
        isVisible={ipBlockedInfo.isBlocked}
        ipAddress={ipBlockedInfo.ipAddress}
      />
      <LgmProductNavigator />
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  loadingContainerStyle: {
    height: '100vh', // h-screen
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: GRAY_50, // bg-gray-50
    fontFamily: 'var(--font-lgm, sans-serif)', // font-lgm
  },
  errorOverlayStyle: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: GRAY_50, // bg-gray-50
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    fontFamily: 'var(--font-lgm, sans-serif)', // font-lgm
  },
  errorModalStyle: {
    backgroundColor: '#ffffff', // bg-white
    borderRadius: '16px', // rounded-2xl
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // shadow-lg
    padding: '32px', // p-8
    textAlign: 'center' as const,
    maxWidth: '28rem', // max-w-md
  },
  errorTitleStyle: {
    fontSize: '20px', // text-xl
    fontWeight: 'bold',
    color: '#dc2626', // text-red-600
    marginBottom: '16px', // mb-4
  },
  errorTextStyle: {
    color: GRAY_700, // text-gray-700
  },
  loginOverlayStyle: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: GRAY_50, // bg-gray-50
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    fontFamily: 'var(--font-lgm, sans-serif)', // font-lgm
  },
  loginModalStyle: {
    backgroundColor: '#ffffff', // bg-white
    borderRadius: '16px', // rounded-2xl
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', // shadow-lg
    padding: '48px', // p-12
    maxWidth: '32rem', // max-w-lg
    width: '100%',
    textAlign: 'center' as const,
  },
  loginLogoStyle: {
    height: '80px', // h-20
    margin: '0 auto 24px auto', // mx-auto mb-6
  },
  loginTitleStyle: {
    fontSize: '30px', // text-3xl
    fontWeight: 'bold',
    marginBottom: '16px', // mb-4
    color: LGM_MAGENTA, // wordmark text
  },
  loginSubtitleStyle: {
    fontSize: '18px', // text-lg
    color: GRAY_600, // text-gray-600
    marginBottom: '32px', // mb-8
  },
  loginButtonStyle: {
    backgroundColor: LGM_ORANGE,
    color: '#ffffff', // text-white
    padding: '12px 32px', // px-8 py-3
    borderRadius: '8px', // rounded-lg
    boxShadow:
      '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)', // shadow-lg
    transition: 'background-color 0.2s', // transition
    fontSize: '18px', // text-lg
    fontWeight: '600', // font-semibold
    border: 'none',
    cursor: 'pointer',
  },
};

export default App;
